/**
 * Channels OAuth Policy
 * 
 * Processes OAuth redirect callbacks:
 * - Extracts code and state from URL
 * - Calls RegisterSalesChannelIntegration with OAuth code
 * - Clears URL params after processing
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';
import { uuidv7 } from 'uuidv7';

/**
 * Creates a policy that processes OAuth redirects
 */
export const createChannelsOAuthPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channels-oauth',

  shouldReact: ( prev, next ) => {
    // React when OAuth processing starts
    return (
      !prev.oauthRedirect.isProcessing &&
      next.oauthRedirect.isProcessing &&
      next.oauthRedirect.code !== null
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const { code, state: oauthState } = next.oauthRedirect;
    
    if ( !code ) {
      console.error( '[channels] OAuth callback missing code' );
      ctx.dispatch(
        { type: 'integrations/channels/oauth/fail', payload: { error: 'Missing code' }},
        { source: 'api' }
      );
      return;
    }

    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- surfaces via CDSE channels/oauth/fail dispatch (domain error state -> UI); outside the heuristic sink list (TV1-2010 waiver, not a rethrow).
    try {
      // Parse state to get channel type (state format: "channelType:randomId" or just channelType)
      const channelType = oauthState?.split( ':' )[0] || 'unknown';

      // Register the channel with OAuth code
      await backend.commands.ProvisionSystemIntegration({
        id: uuidv7(),
        body: {
          connectorId: channelType,
          credentials: {
            code,
            state: oauthState
          }
        } as any
      });

      // Clear URL params
      const url = new URL( window.location.href );
      url.searchParams.delete( 'code' );
      url.searchParams.delete( 'state' );
      window.history.replaceState( null, '', url.pathname );

      ctx.dispatch(
        { type: 'integrations/channels/oauth/complete', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      ctx.dispatch(
        { type: 'integrations/channels/oauth/fail', payload: { error: error instanceof Error ? error.message : 'Unknown error' }},
        { source: 'api' }
      );
    }
  }
});
